// Per-game (product) report drill-down. Reached by clicking a game row in the
// Product P/L or P/L report tables. Composes the existing gameId-filtered report
// endpoints (GGR + top bettors + recent big wins) into one view — no new backend
// endpoint needed. Filters: window (incl. custom range) + currency (native
// model — money metrics are per-currency, never blended). Both tables are
// server-paged.
import { AdminApiError, type AdminClient, type GgrReport } from '@apx/admin-sdk';
import { mountTopBettors, mountRecentWins } from './reports-tables.js';
import { money } from './reports-shared.js';

import { wireDatePickers } from '../date-picker.js';

export interface ReportGameDetailDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  /** Tab to return to ("pl" | "product-pl"). */
  backTab: string;
}

type Since = '24h' | '7d' | '30d';

const SINCE_LABEL: Record<Since, string> = {
  '24h': 'Last 24h',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};


interface Controls {
  since: Since;
  from?: string;
  to?: string;
  custom: boolean;
  error?: string;
}

export async function renderReportGameDetailPage(
  root: HTMLElement,
  deps: ReportGameDetailDeps,
): Promise<void> {
  const backHref = `/integrators/${encodeURIComponent(deps.integratorId)}/reports/${deps.backTab}`;
  const currencies = await loadCurrencies(deps.client, deps.integratorId);
  let currency: string | undefined = currencies[0];
  const currencyOptions = currencies
    .map((c) => `<option value="${escapeHtml(c)}"${c === currency ? ' selected' : ''}>${escapeHtml(c)}</option>`)
    .join('');
  root.innerHTML = `
    <h1 class="page-title">Game breakdown</h1>
    <p class="page-sub">
      <a href="${backHref}" class="row-link">← Back to report</a>
      &middot; <code>${escapeHtml(deps.gameId)}</code>
    </p>
    <div class="card report-filters">
      <div class="filter-row">
        <label class="filter-field">Window
          <select id="rgd-since" class="field-input">
            <option value="24h">Last 24h</option>
            <option value="7d" selected>Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="custom">Custom range…</option>
          </select>
        </label>
        <span id="rgd-custom" class="filter-field-group hidden">
          <span class="filter-field"><label for="rgd-from">From</label><input id="rgd-from" type="datetime-local" step="1" class="field-input" /></span>
          <span class="filter-field"><label for="rgd-to">To</label><input id="rgd-to" type="datetime-local" step="1" class="field-input" /></span>
        </span>
        ${currencies.length > 0 ? `<label class="filter-field">Currency
          <select id="rgd-currency" class="field-input">${currencyOptions}</select>
        </label>` : ''}
        <button id="rgd-run" class="primary">Refresh</button>
        <span id="rgd-msg" class="muted filter-msg"></span>
      </div>
    </div>
    <div id="rgd-body"><p class="muted">Loading…</p></div>
  `;

  wireDatePickers(root);

  const body = root.querySelector<HTMLElement>('#rgd-body')!;
  const sinceSel = root.querySelector<HTMLSelectElement>('#rgd-since')!;
  const customEl = root.querySelector<HTMLElement>('#rgd-custom')!;
  const fromEl = root.querySelector<HTMLInputElement>('#rgd-from')!;
  const toEl = root.querySelector<HTMLInputElement>('#rgd-to')!;
  const runBtn = root.querySelector<HTMLButtonElement>('#rgd-run')!;
  const msgEl = root.querySelector<HTMLElement>('#rgd-msg')!;

  function readControls(): Controls {
    if (sinceSel.value === 'custom') {
      if (!fromEl.value || !toEl.value) {
        return { since: '24h', custom: true, error: 'Pick both From and To for a custom range.' };
      }
      const from = new Date(fromEl.value);
      const to = new Date(toEl.value);
      if (from >= to) return { since: '24h', custom: true, error: 'From must be before To.' };
      return { since: '24h', custom: true, from: from.toISOString(), to: to.toISOString() };
    }
    return { since: sinceSel.value as Since, custom: false };
  }

  async function refresh(): Promise<void> {
    const c = readControls();
    if (c.error) {
      msgEl.textContent = c.error;
      msgEl.className = 'error filter-msg';
      return;
    }
    msgEl.textContent = '';
    msgEl.className = 'muted filter-msg';

    // Common gameId + currency-scoped args shared by every report call here.
    const args = { gameId: deps.gameId, since: c.since, from: c.from, to: c.to, currency };
    const windowLabel = c.custom ? 'Custom range' : SINCE_LABEL[c.since];

    body.innerHTML = `
      <div id="rgd-kpis"><p class="muted">Loading…</p></div>
      <section class="card report-section">
        <h2 class="report-section-title">Top players</h2>
        <div id="rgd-bettors"></div>
      </section>
      <section class="card report-section">
        <h2 class="report-section-title">Recent big wins</h2>
        <div id="rgd-wins"></div>
      </section>
    `;
    const kpisEl = body.querySelector<HTMLElement>('#rgd-kpis')!;
    const bettorsEl = body.querySelector<HTMLElement>('#rgd-bettors')!;
    const winsEl = body.querySelector<HTMLElement>('#rgd-wins')!;

    // KPIs are scoped to the selected currency by the server filter.
    try {
      const ggr = await deps.client.fetchGgrReport(deps.integratorId, args);
      kpisEl.innerHTML = renderKpis(ggr, windowLabel, currency);
    } catch (err) {
      kpisEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }

    // Both tables are server-paged; both are scoped to the selected currency.
    const ctx = { currency: currency ?? '', integratorId: deps.integratorId, client: deps.client };
    mountTopBettors(bettorsEl, ctx, (page, limit) =>
      deps.client.fetchTopBettors(deps.integratorId, { ...args, page, limit }),
    );
    mountRecentWins(winsEl, ctx, (page, limit) =>
      deps.client.fetchRecentBigWins(deps.integratorId, { ...args, page, limit }),
    );
  }

  sinceSel.addEventListener('change', () => {
    customEl.classList.toggle('hidden', sinceSel.value !== 'custom');
    if (sinceSel.value !== 'custom') void refresh();
  });
  root.querySelector<HTMLSelectElement>('#rgd-currency')?.addEventListener('change', (e) => {
    currency = (e.target as HTMLSelectElement).value || undefined;
    void refresh();
  });
  runBtn.addEventListener('click', () => void refresh());

  await refresh();
}

function renderKpis(g: GgrReport, windowLabel: string, ccy: string | undefined): string {
  // Server-scoped to one currency — the credit-native totals ARE that
  // currency's amounts. RTP/bets/players are unit-agnostic ratios/counts.
  const fmt = (v: string): string => money(v, ccy);
  const houseColor = Number(g.houseTake) >= 0 ? 'var(--success)' : 'var(--error)';
  const cards: ReadonlyArray<{ label: string; value: string; color?: string }> = [
    { label: 'Turnover', value: fmt(g.totalBet) },
    { label: 'Payout', value: fmt(g.totalPayout) },
    { label: 'House take (GGR)', value: fmt(g.houseTake), color: houseColor },
    { label: 'RTP', value: `${(g.rtp * 100).toFixed(2)}%` },
    { label: 'Bets', value: g.betCount.toLocaleString() },
    { label: 'Distinct players', value: g.distinctPlayers.toLocaleString() },
  ];
  return `<p class="muted" style="margin:0 0 10px">${escapeHtml(windowLabel)}${ccy ? ` · ${escapeHtml(ccy)}` : ''}</p>
    <div class="players-stats">${cards
      .map(
        (c) => `<div class="stat-card">
          <div class="stat-card-label">${c.label}</div>
          <div class="stat-card-value"${c.color ? ` style="color:${c.color}"` : ''}>${c.value}</div>
        </div>`,
      )
      .join('')}</div>`;
}

/** Enabled currencies this integrator trades in — powers the currency filter. */
async function loadCurrencies(client: AdminClient, integratorId: string): Promise<string[]> {
  try {
    const rows = await client.listIntegratorFiatCurrencies(integratorId);
    return rows
      .filter((r) => r.enabled)
      .map((r) => r.fiatCurrency)
      .sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}
